import { z } from 'zod';
import { IdSchema, E164Schema, TimestampSchema, MoneySchema } from './common.js';

/**
 * The Node API contract (doc 05 section 10). Consumed only by arc-agent.
 *
 * CDRs arrive NORMALISED. FreeSWITCH's json_cdr is large, deeply nested and
 * version-specific -- parsing it belongs on the node, next to the switch that
 * produced it, not in the control plane. That keeps a FreeSWITCH upgrade from
 * becoming an arc-api change.
 */

export const NodeCdrSchema = z.object({
  /** FreeSWITCH channel UUID. The idempotency key for the whole pipeline. */
  id: IdSchema,
  call_uuid: IdSchema,
  tenant_id: IdSchema,
  direction: z.enum(['inbound', 'outbound', 'internal']),
  leg: z.enum(['a', 'b']).default('a'),

  from_number: z.string().max(64).nullable().optional(),
  from_name: z.string().max(128).nullable().optional(),
  to_number: z.string().max(64).nullable().optional(),
  destination_e164: E164Schema.nullable().optional(),
  extension_id: IdSchema.nullable().optional(),

  started_at: TimestampSchema,
  answered_at: TimestampSchema.nullable().optional(),
  ended_at: TimestampSchema,
  duration_sec: z.number().int().min(0),
  billable_sec: z.number().int().min(0).default(0),

  hangup_cause: z.string().max(64).nullable().optional(),
  sip_code: z.number().int().nullable().optional(),

  mos: z.string().nullable().optional(),
  jitter_ms: z.string().nullable().optional(),
  packet_loss_pct: z.string().nullable().optional(),
  codec: z.string().max(32).nullable().optional(),

  /** What the NODE computed, so the control plane can verify it (ADR-015). */
  rated_on_node: MoneySchema.nullable().optional(),
  rate_deck_version_id: IdSchema.nullable().optional(),

  recording_path: z.string().max(512).nullable().optional(),
});
export type NodeCdr = z.infer<typeof NodeCdrSchema>;

export const NodeCdrBatchSchema = z.object({
  cdrs: z.array(NodeCdrSchema).min(1).max(500),
}).strict();

export const NodeHeartbeatSchema = z.object({
  software: z.record(z.string()).optional(),
  active_calls: z.number().int().min(0).default(0),
  registrations: z.number().int().min(0).default(0),
  cdr_spool_depth: z.number().int().min(0).default(0),
}).strict();
